import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FIRE_COLORS } from '../BlazeMaterial';

// MAGMA IMPACT - Block lands with molten impact effect
const EmberBlockPlaceEffect = ({ position, onComplete }) => {
  const groupRef = useRef();
  const startTime = useRef(Date.now());

  // Impact flash refs
  const flashRef = useRef();
  const crackRingsRef = useRef([]);
  const embersRef = useRef([]);
  const heatWaveRef = useRef();
  const steamRef = useRef([]);

  // Ember burst particles
  const emberData = useMemo(() => {
    const data = [];
    for (let i = 0; i < 10; i++) {
      const angle = (i / 10) * Math.PI * 2 + Math.random() * 0.3;
      data.push({
        angle,
        speed: 1.5 + Math.random() * 1.5,
        upward: 1 + Math.random() * 0.8,
        size: 0.04 + Math.random() * 0.03,
        phase: Math.random() * Math.PI * 2,
      });
    }
    return data;
  }, []);

  // Crack rings that spread outward
  const crackData = useMemo(() => {
    return [
      { delay: 0, maxScale: 1.2, width: 0.08 },
      { delay: 0.05, maxScale: 0.9, width: 0.06 },
      { delay: 0.1, maxScale: 0.6, width: 0.04 },
    ];
  }, []);

  // Steam wisps rising
  const steamData = useMemo(() => {
    const data = [];
    for (let i = 0; i < 4; i++) {
      data.push({
        x: (Math.random() - 0.5) * 0.6,
        speed: 1.2 + Math.random() * 0.6,
        size: 0.08 + Math.random() * 0.04,
        delay: Math.random() * 0.1,
      });
    }
    return data;
  }, []);

  useFrame(() => {
    if (!groupRef.current) return;

    const elapsed = (Date.now() - startTime.current) / 1000;
    const duration = 0.4;

    if (elapsed > duration) {
      onComplete?.();
      return;
    }

    const progress = elapsed / duration;

    // Central impact flash - bright hot flash
    if (flashRef.current) {
      const flashProgress = Math.min(progress * 3, 1);
      const flashFade = flashProgress > 0.3 ? 1 - (flashProgress - 0.3) / 0.7 : 1;
      flashRef.current.material.opacity = 0.9 * flashFade;
      flashRef.current.scale.setScalar(0.3 + flashProgress * 0.8);
    }

    // Expanding crack rings
    crackRingsRef.current.forEach((ring, i) => {
      if (!ring) return;
      const data = crackData[i];
      const ringProgress = Math.max(0, (elapsed - data.delay) / 0.25);

      if (ringProgress <= 0 || ringProgress > 1) {
        ring.visible = false;
        return;
      }

      ring.visible = true;
      const easeOut = 1 - Math.pow(1 - ringProgress, 2);
      ring.scale.setScalar(easeOut * data.maxScale);
      ring.material.opacity = (1 - ringProgress) * 0.8;
    });

    // Heat wave distortion
    if (heatWaveRef.current) {
      const waveProgress = progress;
      if (waveProgress < 0.6) {
        heatWaveRef.current.visible = true;
        const easeOut = 1 - Math.pow(1 - waveProgress / 0.6, 2);
        heatWaveRef.current.scale.setScalar(0.5 + easeOut * 1.5);
        heatWaveRef.current.material.opacity = (1 - waveProgress / 0.6) * 0.4;
      } else {
        heatWaveRef.current.visible = false;
      }
    }

    // Flying embers
    embersRef.current.forEach((ember, i) => {
      if (!ember) return;
      const data = emberData[i];

      const distance = data.speed * progress;
      ember.position.x = Math.cos(data.angle) * distance;
      ember.position.y = Math.sin(data.angle) * distance * 0.4 + progress * data.upward - progress * progress * 2;
      ember.position.z = 0.1;

      // Flickering
      const flicker = Math.sin(elapsed * 25 + data.phase) * 0.3 + 0.7;
      ember.material.opacity = (1 - progress) * flicker * 0.9;
      ember.scale.setScalar(data.size * (1 - progress * 0.5));
    });

    // Rising steam
    steamRef.current.forEach((steam, i) => {
      if (!steam) return;
      const data = steamData[i];
      const steamProgress = Math.max(0, elapsed - data.delay) / 0.35;

      if (steamProgress <= 0 || steamProgress > 1) {
        steam.visible = false;
        return;
      }

      steam.visible = true;
      steam.position.x = data.x + Math.sin(elapsed * 8 + i) * 0.05;
      steam.position.y = 0.2 + steamProgress * data.speed * 0.5;
      steam.scale.setScalar(data.size * (1 + steamProgress));
      steam.material.opacity = (1 - steamProgress) * 0.3;
    });
  });

  return (
    <group ref={groupRef} position={position}>
      {/* Central impact flash - white hot */}
      <mesh ref={flashRef} position={[0, 0, 0.15]}>
        <circleGeometry args={[0.4, 12]} />
        <meshBasicMaterial
          color={FIRE_COLORS.flameCore}
          transparent
          opacity={0.9}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Crack rings spreading */}
      {crackData.map((data, i) => (
        <mesh
          key={`crack-${i}`}
          ref={(el) => crackRingsRef.current[i] = el}
          position={[0, 0, 0.12]}
          visible={false}
        >
          <ringGeometry args={[0.3 - data.width, 0.3, 8]} />
          <meshBasicMaterial
            color={FIRE_COLORS.ember}
            transparent
            opacity={0.8}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Heat wave distortion */}
      <mesh ref={heatWaveRef} position={[0, 0, 0.08]} visible={false}>
        <ringGeometry args={[0.4, 0.6, 16]} />
        <meshBasicMaterial
          color={FIRE_COLORS.heatDistort}
          transparent
          opacity={0.4}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Flying ember particles */}
      {emberData.map((data, i) => (
        <mesh
          key={`ember-${i}`}
          ref={(el) => embersRef.current[i] = el}
        >
          <circleGeometry args={[data.size, 5]} />
          <meshBasicMaterial
            color={i % 3 === 0 ? FIRE_COLORS.flameCore : FIRE_COLORS.ember}
            transparent
            opacity={0.9}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Rising steam wisps */}
      {steamData.map((data, i) => (
        <mesh
          key={`steam-${i}`}
          ref={(el) => steamRef.current[i] = el}
          position={[data.x, 0.2, 0.1]}
          visible={false}
        >
          <circleGeometry args={[data.size, 6]} />
          <meshBasicMaterial
            color={FIRE_COLORS.smoke}
            transparent
            opacity={0.3}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

export default EmberBlockPlaceEffect;
